/**
 * The jump button ("hold anywhere = jump") as a timeline of timestamped changes.
 *
 * Several sources can hold it at once (fingers, the mouse, Space / ArrowUp / W, a dev bot);
 * the jump is held while any of them is. Every change of that combined state is queued with
 * the time of the DOM event that caused it (`event.timeStamp`, performance.now() timebase).
 * The game loop then applies each change at the simulation tick that covers that moment
 * (`heldForTick`) instead of the frame in which it was noticed: on a 60 Hz display a press
 * would otherwise land up to a whole frame (two 120 Hz ticks) late, at random.
 */

/** Pending changes kept at most; far more than any frame produces. */
const CAPACITY = 64;

export class JumpInput {
  private readonly sources = new Set<string>();
  /** Ring buffer of combined-state changes, oldest first. */
  private readonly times = new Float64Array(CAPACITY);
  private readonly states = new Uint8Array(CAPACITY);
  private head = 0;
  private count = 0;
  private lastTime = Number.NEGATIVE_INFINITY;
  /** Value returned by the last heldForTick(). */
  private reported = false;

  /** True while any source holds the button (including changes not applied yet). */
  get held(): boolean {
    return this.sources.size > 0;
  }

  /** Changes waiting for their tick. */
  get pending(): number {
    return this.count;
  }

  /** `source` (e.g. "pointer:3", "key:Space") went down at `time`. Repeats are ignored. */
  press(source: string, time: number): void {
    if (this.sources.has(source)) return;
    this.sources.add(source);
    if (this.sources.size === 1) this.enqueue(time, true);
  }

  /** `source` went up at `time`. Unknown sources are ignored. */
  release(source: string, time: number): void {
    if (!this.sources.delete(source)) return;
    if (this.sources.size === 0) this.enqueue(time, false);
  }

  /** Everything up (pointer cancelled, window lost focus, page hidden, menu opened). */
  releaseAll(time: number): void {
    if (this.sources.size === 0) return;
    this.sources.clear();
    this.enqueue(time, false);
  }

  /**
   * The input for a simulation tick that ends at real time `end`: applies the changes made
   * before `end`. At most one change of the value is applied per tick, so a tap shorter than
   * a tick still holds the button for one whole tick (the physics sees the press), and a
   * release + new press within one tick becomes a press on the next tick (never lost).
   */
  heldForTick(end: number): boolean {
    let value = this.reported;
    let changed = false;
    while (this.count > 0) {
      const i = this.head;
      if (this.times[i]! >= end) break;
      const next = this.states[i] === 1;
      if (next !== value) {
        if (changed) break;
        value = next;
        changed = true;
      }
      this.head = (i + 1) % CAPACITY;
      this.count--;
    }
    this.reported = value;
    return value;
  }

  /**
   * Forgets the pending changes and treats the button as released from now on: whatever is
   * held at this moment only counts after it is let go and pressed again. The holding
   * sources are forgotten too (their release changes nothing), so another finger — or the
   * same source pressing again — counts at once instead of waiting for the old hold to end.
   */
  discard(): void {
    this.sources.clear();
    this.head = 0;
    this.count = 0;
    this.reported = false;
  }

  private enqueue(time: number, held: boolean): void {
    // Sources report in event order; keep the queue monotonic even if a browser's clocks
    // disagree by a hair between event types.
    const at = Math.max(time, this.lastTime);
    this.lastTime = at;
    if (this.count === CAPACITY) {
      // Nobody consumes (should not happen while racing): drop the oldest change.
      this.head = (this.head + 1) % CAPACITY;
      this.count--;
    }
    const i = (this.head + this.count) % CAPACITY;
    this.times[i] = at;
    this.states[i] = held ? 1 : 0;
    this.count++;
  }
}
